import { cva, type VariantProps } from "class-variance-authority";
import {
	createContext,
	type FC,
	type HTMLAttributes,
	type MouseEventHandler,
	type Ref,
	useContext,
} from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";
import { _BaseButton } from "./_BaseButton";
import { Icon, type IconSize } from "./Icon";

const avatarVariants = cva(
	"flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-bc-strong bg-surface-base",
	{
		variants: {
			size: {
				sm: "h-6 w-6",
				md: "h-7 w-7",
				lg: "h-8 w-8",
			},
		},
		defaultVariants: { size: "md" },
	},
);

const avatarLabelVariants = cva("select-none font-medium uppercase leading-none text-primary", {
	variants: {
		size: {
			sm: "text-[10px]",
			md: "text-[12px]",
			lg: "text-[14px]",
		},
	},
	defaultVariants: { size: "md" },
});

type AvatarVariantProps = VariantProps<typeof avatarVariants>;

export type AvatarSize = NonNullable<AvatarVariantProps["size"]>;

export const AVATAR_SIZES = ["sm", "md", "lg"] as const satisfies readonly AvatarSize[];

const DEFAULT_AVATAR_SIZE: AvatarSize = "md";

/** Named avatar size → the `Icon` size the initials-less fallback glyph renders at. */
const fallbackIconSizes: Record<AvatarSize, IconSize> = {
	sm: "micro",
	md: "mini",
	lg: "small",
};

/**
 * Set by `AvatarGroup` so stacked avatars size together without the consumer repeating
 * `size` on every child. An explicit `size` prop always wins.
 */
export const AvatarSizeContext = createContext<AvatarSize | undefined>(undefined);

/** Resolves the rendered size: explicit prop → enclosing `AvatarGroup` → `md`. */
export const useAvatarSize = (size: AvatarVariantProps["size"]): AvatarSize => {
	const groupSize = useContext(AvatarSizeContext);
	return size ?? groupSize ?? DEFAULT_AVATAR_SIZE;
};

const toInitials = (firstName?: string, lastName?: string): string =>
	`${firstName?.trim().charAt(0) ?? ""}${lastName?.trim().charAt(0) ?? ""}`;

/** Hover affordance applied to the circle when the avatar is wrapped in a button. */
const interactiveClasses = "transition-colors group-hover:border-bc-heavy";

export type AvatarProps = Omit<HTMLAttributes<HTMLDivElement>, "onClick"> & {
	/** The user's first name — only its first character is rendered. */
	firstName?: string;

	/** The user's last name — only its first character is rendered. */
	lastName?: string;

	/**
	 * Makes the avatar clickable. When set, the circle is wrapped in a `_BaseButton` so native
	 * `<button>` semantics (keyboard, focus, `disabled`) come for free and the outline darkens
	 * on hover. Pair it with `aria-label` (or `aria-labelledby`) — the initials alone are a poor
	 * accessible name, and the fallback glyph leaves the button with none at all.
	 */
	onClick?: MouseEventHandler<HTMLElement>;

	/**
	 * Diameter of the circle (sm: 24px, md: 28px, lg: 32px). Defaults to the size provided by
	 * an enclosing `AvatarGroup`, then to `md`.
	 */
	size?: AvatarVariantProps["size"];

	/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
	trackingId?: string;

	ref?: Ref<HTMLDivElement>;
};

/**
 * Avatar primitive. A gray-outlined circle holding a user's initials, derived from
 * `firstName`/`lastName`. When neither yields an initial it falls back to the filled user
 * glyph in the primary color.
 *
 * Pass `onClick` to make it interactive — the circle is wrapped in a `_BaseButton` so callers
 * never need their own `<button>`. The forwarded ref always points at the circle itself, but
 * `aria-label`/`aria-labelledby` land on the `<button>` so the interactive element is the one
 * carrying the accessible name.
 *
 * Reach for `AvatarGroup` when rendering more than one.
 */
export const Avatar: FC<AvatarProps> = ({
	firstName,
	lastName,
	size,
	onClick,
	trackingId,
	className,
	ref,
	"aria-label": ariaLabel,
	"aria-labelledby": ariaLabelledBy,
	...rest
}) => {
	const resolvedSize = useAvatarSize(size);
	const initials = toInitials(firstName, lastName);
	const labelAttrs = { "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy };

	const circle = (
		<div
			ref={ref}
			className={cn(
				avatarVariants({ size: resolvedSize }),
				onClick && interactiveClasses,
				className,
			)}
			{...trackingAttrs(trackingId)}
			{...(onClick ? {} : labelAttrs)}
			{...rest}
		>
			{initials ? (
				<span className={avatarLabelVariants({ size: resolvedSize })}>{initials}</span>
			) : (
				<Icon type="user" filled color="primary" size={fallbackIconSizes[resolvedSize]} />
			)}
		</div>
	);

	if (!onClick) return circle;

	return (
		<_BaseButton
			onClick={onClick}
			className="group flex cursor-pointer rounded-full"
			{...labelAttrs}
		>
			{circle}
		</_BaseButton>
	);
};
Avatar.displayName = "Avatar";

/**
 * The `+N` circle `AvatarGroup` renders in place of the avatars it collapsed. Lives here rather
 * than in `AvatarGroup` so it shares the avatar circle and label styling directly instead of the
 * variants leaking out of this file. Not part of the public API — the barrel omits it.
 */
export const AvatarOverflowBadge = ({
	count,
	size,
	className,
}: {
	count: number;
	size: AvatarSize;
	className?: string;
}) => (
	<div
		className={cn(avatarVariants({ size }), className)}
		data-testid="avatar-group-overflow"
		role="img"
		aria-label={`${count} more`}
	>
		<span className={avatarLabelVariants({ size })}>+{count}</span>
	</div>
);
